import axios from 'axios';
import router from '../router';
import config from '../config';

class TokenService {
  constructor() {
    this.cache = {
      token: null,
      expiresAt: null
    };
  }

  async getApiToken() {
    try {
      if (this.cache.token && this.cache.expiresAt && Date.now() < this.cache.expiresAt) {
        return this.cache.token;
      }

      const response = await axios.get(`${config.apiBaseUrl}/api/auth/session-token`, {
        withCredentials: true,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.data?.token) {
        this.cache.token = response.data.token;
        this.cache.expiresAt = Date.now() + (25 * 60 * 1000);
        return response.data.token;
      }

      return null;
    } catch (error) {
      console.error('Failed to get API token:', error);
      return null;
    }
  }

  async refreshApiToken() {
    try {
      this.cache.token = null;
      this.cache.expiresAt = null;

      return await this.getApiToken();
    } catch (error) {
      console.error('Failed to refresh API token:', error);
      return null;
    }
  }

  async executeAuthenticatedRequest(requestFn) {
    try {
      let token = await this.getApiToken();
      
      if (!token) {
        throw new Error('Unable to obtain API token');
      }

      const result = await requestFn(token);
      return result;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Token expired, attempting refresh...');
        
        const newToken = await this.refreshApiToken();
        if (newToken) {
          try {
            return await requestFn(newToken);
          } catch (retryError) {
            console.error('Request failed after token refresh:', retryError);
            throw retryError;
          }
        }
      }
      
      throw error;
    }
  }

  async executeWithApiToken(requestFn) {
    return this.executeAuthenticatedRequest(requestFn);
  }

  clearCache() {
    this.cache.token = null;
    this.cache.expiresAt = null;
  }

  async isUserAuthenticated() {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, {
        withCredentials: true
      });
      
      return response.data?.isAuthenticated || false;
    } catch (error) {
      console.error('Failed to check user authentication:', error);
      return false;
    }
  }

  async getCurrentUser() {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, {
        withCredentials: true
      });
      
      if (response.data?.isAuthenticated && response.data?.user) {
        return response.data.user;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  async logout() {
    try {
      await axios.post(`${config.apiBaseUrl}/api/auth/logout`, {}, {
        withCredentials: true
      });
      
      this.clearCache();
      
      router.push('/');
      
      return true;
    } catch (error) {
      console.error('Logout failed:', error);

      this.clearCache();
      router.push('/');
      return false;
    }
  }
}

const tokenService = new TokenService();
export default tokenService;